import { InfoBox } from "@/components/portfolio/TenantInfoBox";
import { OverviewSectionBadge } from "@/components/portfolio/all-estate-overview/OverviewSectionBadge";
import { SingleEstateInfoPanel } from "@/components/portfolio/single-estate-overview/SingleEstateInfoPanel";
import { HintWindow } from "@/components/ui/HintWindow";
import { Panel, PanelGrid } from "@/components/ui/Panel";
import { RoundButtonWithIcon } from "@/components/tools/RoundButtonWithIcon";
import { RoundButtonWithToggleIcon } from "@/components/tools/RoundButtonWithToggleIcon";
import { TrashButton } from "@/components/tools/TrashButton";
import { Card, CardContent, CardHeader } from "@/components/ui/Card";
import { useMenuItemsContext } from "@/context/MenuItemsContext";
import { useWatchDogContext } from "@/context/WatchDogContext";

import {
  MapPinIcon as MapPinOutlineIcon,
  BellAlertIcon,
  FunnelIcon,
  BanknotesIcon,
  BuildingOffice2Icon,
  ClockIcon,
  EnvelopeIcon,
  HomeModernIcon,
  MagnifyingGlassIcon,
  MapIcon,
  Squares2X2Icon,
  Cog6ToothIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";

import { PauseIcon, PencilIcon, PlayIcon } from "@heroicons/react/24/solid";
import { PortfolioPageSectionBadge } from "@/components/portfolio/portfolio-page/PortfolioPageSectionBadge";
import { getWatchDogIcon } from "@/components/hlidaci-pes/watchdog-page/watchDogOverviewUtils";
import { RoundButtonsContainer } from "@/components/tools/RoundButtonsContainer";
import { useLanguage } from "@/context/LanguageContext";
import {
  getEstateValueLabel,
  getEstateValueListLabel,
} from "@/utils/estateValueLabels";

function getEmailFrequencyLabel(
  emailReportFrequency: string,
  copy: {
    immediately: string;
    everyTwoHours: string;
    dailySummary: string;
    frequencyNotSet: string;
  },
) {
  return (
    {
      NONSTOP: copy.immediately,
      OFTEN: copy.everyTwoHours,
      DAILY: copy.dailySummary,
    }[emailReportFrequency] ?? copy.frequencyNotSet
  );
}

function getEmailSettingLabel(
  emailReportSetting: string,
  copy: {
    eachOffer: string;
    thisWatchdog: string;
    allTogether: string;
    deliveryNotSet: string;
  },
) {
  return (
    {
      SINGLE: copy.eachOffer,
      GROUP_BY_WATCHDOG: copy.thisWatchdog,
      GROUP_BY_ALL: copy.allTogether,
    }[emailReportSetting] ?? copy.deliveryNotSet
  );
}

function normalizeDisplayText(value: string | null | undefined) {
  if (value == null) {
    return null;
  }

  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return null;
  }

  const normalizedValue = trimmedValue.toLowerCase();

  if (
    normalizedValue === "null" ||
    normalizedValue === "undefined" ||
    normalizedValue === "nezadáno"
  ) {
    return null;
  }

  return trimmedValue;
}

function normalizeRangeValue(value: number | string | null | undefined) {
  if (value == null) {
    return null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  return normalizeDisplayText(value);
}

function formatRange(
  valueFrom: number | string | null | undefined,
  valueTo: number | string | null | undefined,
  suffix = "",
  prefixFrom = "Od",
  prefixTo = "Do",
) {
  const normalizedValueFrom = normalizeRangeValue(valueFrom);
  const normalizedValueTo = normalizeRangeValue(valueTo);

  if (normalizedValueFrom == null && normalizedValueTo == null) {
    return null;
  }

  if (normalizedValueFrom != null && normalizedValueTo != null) {
    return `${normalizedValueFrom}${suffix} - ${normalizedValueTo}${suffix}`;
  }

  if (normalizedValueFrom != null) {
    return `${prefixFrom} ${normalizedValueFrom}${suffix}`;
  }

  return `${prefixTo} ${normalizedValueTo}${suffix}`;
}

const SelectedDogCard = () => {
  const { language } = useLanguage();
  const copy = {
    cs: {
      around: "V okolí {value}",
      currencySuffix: " Kč",
      floorSuffix: ". patro",
      from: "Od",
      to: "Do",
      selectedWatchdog: "Vybraný hlídací pes",
      editWatchdog: "Upravit hlídacího psa",
      deleteWatchdog: "Smazat hlídacího psa",
      deleteWatchdogTitle: "Opravdu chcete smazat hlídacího psa?",
      deleteWatchdogDescription:
        "Tuto akci nelze vrátit zpět. Hlídací pes i jeho výsledky budou trvale odstraněny.",
      resumeWatchdog: "Obnovit hlídacího psa",
      pauseWatchdog: "Pozastavit hlídacího psa",
      settings: "Nastavení",
      parameters: "Parametry",
      detailedSearch: "Podrobnější hledání",
      notifications: "Notifikace",
      notSet: "Nezadáno",
      emailOff:
        "Emailové upozornění je vypnuté. Zapnete ho v detailu hlídacího psa.",
      enabled: "Zapnuto",
      immediately: "Bezprostředně",
      everyTwoHours: "Shrnutí každé 2 hodiny",
      dailySummary: "Shrnutí za poslední den",
      frequencyNotSet: "Frekvence není nastavená",
      eachOffer: "Každou nabídku zvlášť",
      thisWatchdog: "Tento hlídací pes zvlášť",
      allTogether: "Vše dohromady v 1 e-mailu",
      deliveryNotSet: "Způsob doručení není nastavený",
    },
    en: {
      around: "Around {value}",
      currencySuffix: " CZK",
      floorSuffix: ". floor",
      from: "From",
      to: "To",
      selectedWatchdog: "Selected watchdog",
      editWatchdog: "Edit watchdog",
      deleteWatchdog: "Delete watchdog",
      deleteWatchdogTitle: "Do you really want to delete this watchdog?",
      deleteWatchdogDescription:
        "This action cannot be undone. The watchdog and its results will be permanently deleted.",
      resumeWatchdog: "Resume watchdog",
      pauseWatchdog: "Pause watchdog",
      settings: "Settings",
      parameters: "Parameters",
      detailedSearch: "Detailed search",
      notifications: "Notifications",
      notSet: "Not set",
      emailOff:
        "Email alerts are off. You can turn them on in the watchdog detail.",
      enabled: "Enabled",
      immediately: "Immediately",
      everyTwoHours: "Summary every 2 hours",
      dailySummary: "Summary for the last day",
      frequencyNotSet: "Frequency is not set",
      eachOffer: "Each offer separately",
      thisWatchdog: "This watchdog separately",
      allTogether: "Everything together in 1 email",
      deliveryNotSet: "Delivery method is not set",
    },
  }[language];
  const { selectedDog, setWatchDogAsActive, deleteWatchDog } =
    useWatchDogContext();

  const { openEditModal } = useMenuItemsContext();

  if (!selectedDog) {
    return null;
  }

  const WatchDogIcon = getWatchDogIcon(selectedDog.estateType);
  const locationLabel =
    normalizeDisplayText(selectedDog.searchAddress) ||
    normalizeDisplayText(selectedDog.exactAddress);
  const localizedLocationLabel = getEstateValueLabel(locationLabel, language);
  const searchRadiusValue = normalizeDisplayText(selectedDog.inRange);
  const categories = [
    ...selectedDog.flatCategory,
    ...selectedDog.homeCategory,
    ...selectedDog.landCategory,
    ...selectedDog.comercialCategory,
    ...selectedDog.otherCategory,
  ].filter(Boolean);

  const searchRadius = searchRadiusValue
    ? copy.around.replace("{value}", searchRadiusValue)
    : null;
  const areaRange = formatRange(
    selectedDog.usableAreaFrom,
    selectedDog.usableAreaTo,
    " m²",
    copy.from,
    copy.to,
  );
  const priceRange = formatRange(
    selectedDog.priceFrom,
    selectedDog.priceTo,
    copy.currencySuffix,
    copy.from,
    copy.to,
  );
  const floorRange = formatRange(
    selectedDog.floorFrom,
    selectedDog.floorTo,
    copy.floorSuffix,
    copy.from,
    copy.to,
  );
  const localizedCategories = getEstateValueListLabel(categories, language);
  const localizedEstateCondition = getEstateValueListLabel(
    selectedDog.estateCondition,
    language,
  );
  const searchDetailsCount = [
    searchRadius,
    areaRange,
    priceRange,
    floorRange,
  ].filter(Boolean).length;
  const filterDetailsCount = [
    categories.length ? localizedCategories : null,
    selectedDog.estateCondition.length ? localizedEstateCondition : null,
  ].filter(Boolean).length;
  const notificationDetailsCount = selectedDog.doEmailReport ? 3 : 0;

  return (
    <HintWindow>
      <Panel variant="shell">
        <div className="same-line w-full sm:justify-between">
          <PortfolioPageSectionBadge type={"dark"}>
            {copy.selectedWatchdog}
          </PortfolioPageSectionBadge>
          <RoundButtonsContainer>
            <RoundButtonWithIcon
              icon={PencilIcon}
              text={copy.editWatchdog}
              onClick={openEditModal}
            />
            <TrashButton
              label={copy.deleteWatchdog}
              confirmTitle={copy.deleteWatchdogTitle}
              confirmDescription={copy.deleteWatchdogDescription}
              onClick={deleteWatchDog}
            />

            <RoundButtonWithToggleIcon
              icon={selectedDog.isEnabled ? PauseIcon : PlayIcon}
              isActive={!selectedDog.isEnabled}
              onClick={() => setWatchDogAsActive(!selectedDog.isEnabled)}
              tooltipTextActive={copy.resumeWatchdog}
              tooltipTextInactive={copy.pauseWatchdog}
            />
          </RoundButtonsContainer>
        </div>
        <PanelGrid className="xl:grid-cols-4">
          <SingleEstateInfoPanel badge={copy.settings} icon={Cog6ToothIcon}>
            <InfoBox
              value={getEstateValueLabel(selectedDog.offerType, language)}
              Icon={SparklesIcon}
            />{" "}
            <InfoBox
              value={getEstateValueLabel(selectedDog.estateType, language)}
              Icon={WatchDogIcon}
            />
            <InfoBox value={localizedLocationLabel} Icon={MapPinOutlineIcon} />
          </SingleEstateInfoPanel>

          <SingleEstateInfoPanel
            badge={copy.parameters}
            icon={MagnifyingGlassIcon}
          >
            {searchDetailsCount > 0 ? (
              <>
                <InfoBox value={searchRadius} Icon={MapIcon} />
                <InfoBox value={areaRange} Icon={Squares2X2Icon} />
                <InfoBox value={priceRange} Icon={BanknotesIcon} />
                <InfoBox value={floorRange} Icon={BuildingOffice2Icon} />
              </>
            ) : (
              <InfoBox value={copy.notSet} Icon={null} />
            )}
          </SingleEstateInfoPanel>

          <SingleEstateInfoPanel
            badge={copy.detailedSearch}
            icon={FunnelIcon}
            emptyMessage=""
          >
            {filterDetailsCount > 0 ? (
              <>
                <InfoBox
                  value={categories.length ? localizedCategories : undefined}
                  Icon={HomeModernIcon}
                />
                <InfoBox
                  value={
                    selectedDog.estateCondition.length
                      ? localizedEstateCondition
                      : undefined
                  }
                  Icon={null}
                />
              </>
            ) : (
              <InfoBox value={copy.notSet} Icon={null} />
            )}
          </SingleEstateInfoPanel>

          <SingleEstateInfoPanel
            badge={copy.notifications}
            icon={BellAlertIcon}
            emptyMessage={copy.emailOff}
          >
            {notificationDetailsCount > 0 ? (
              <>
                <InfoBox value={copy.enabled} Icon={EnvelopeIcon} />
                <InfoBox
                  value={getEmailFrequencyLabel(
                    selectedDog.emailReportFrequency,
                    copy,
                  )}
                  Icon={ClockIcon}
                />
                <InfoBox
                  value={getEmailSettingLabel(
                    selectedDog.emailReportSetting,
                    copy,
                  )}
                  Icon={BellAlertIcon}
                />
              </>
            ) : null}
          </SingleEstateInfoPanel>
        </PanelGrid>
      </Panel>
    </HintWindow>
  );
};

export default SelectedDogCard;
